import { PaperPlaneRight } from '@phosphor-icons/react';
import { EventSource } from 'eventsource';
import { getAuth } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { Input } from 'src/components/Input';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNavigation } from 'src/contexts/NavigationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { cn } from 'src/utils/className';
import { useChatAgent } from '../contexts/ChatAgentContext';
import { AgentCaller } from './AgentCaller';

const PROMPT_STREAM_ENDPOINT = `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/interactive_chat/stream`;
const INITIAL_BOT_MESSAGE = `Hi! I'm here to help shape your voice AI. What problem are you trying to solve with calls?`;
const UPDATED_AGENT_MESSAGE = `I've updated your agent. Try it out and let me know how it goes!`;

export const Chat = () => {
  const auth = getAuth();
  const { post } = useAuthedRequest();
  const { environment } = useEnvironment();
  const { setBlocked } = useNavigation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { agent, setAgent } = useChatAgent();
  const [token, setToken] = useState<string | undefined>();
  const [transcriptMessages, setTranscriptMessages] = useState<
    {
      timestamp: string;
      speaker: 'HUMAN' | 'BOT';
      message: string;
    }[]
  >([
    {
      timestamp: new Date().toISOString(),
      speaker: 'BOT',
      message: INITIAL_BOT_MESSAGE,
    },
  ]);
  const [message, setMessage] = useState<string>('');
  const [conversationId, setConversationId] = useState<string>('');
  const [responseGenerating, setResponseGenerating] = useState<boolean>(false);
  const [isDataMessage, setIsDataMessage] = useState<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!responseGenerating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [responseGenerating]);

  useEffect(() => {
    const fetchConversationId = async () => {
      if (conversationId) return;
      const response = await post('/prompt-builder/conversations', {});
      setConversationId(response.id);
    };
    fetchConversationId();
  }, []);

  const addBotThinkingMessage = () => {
    setResponseGenerating(true);
    setTranscriptMessages((prev) => [
      ...prev,
      { timestamp: new Date().toISOString(), speaker: 'BOT', message: '...' },
    ]);
  };

  const updateBotMessage = (message: string) => {
    setTranscriptMessages((prev) => {
      const lastBotMessageIndex = prev.findLastIndex(
        (msg) => msg.speaker === 'BOT',
      );
      return prev.map((msg, index) =>
        lastBotMessageIndex === index ? { ...msg, message } : msg,
      );
    });
  };

  const fetchBotResponse = async (lastMessage: string) => {
    let completeMessage = '';
    const query = new URLSearchParams({
      last_message: lastMessage,
      conversation_id: conversationId,
    });
    const eventSource = new EventSource(
      `${PROMPT_STREAM_ENDPOINT}?${query.toString()}`,
      {
        // Pass auth for SSE
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${token}`,
              'x-env': environment?.envId || '',
            },
          }),
      },
    );
    eventSource.onmessage = async (event) => {
      const response = JSON.parse(event.data);
      // Check for chat response. Used to prevent tool call responses from closing the event source.
      if (response.type === 'completed' && !!completeMessage) {
        eventSource.close();
        setResponseGenerating(false);
        try {
          const data = JSON.parse(completeMessage);
          setAgent((prev) =>
            Object.assign({}, prev, {
              name: data.name,
              initial_message: data.initial_message,
              voicemail_message: data.voicemail_message,
              prompt: {
                content: data.prompt,
              },
            }),
          );
          setBlocked(true);
          updateBotMessage(UPDATED_AGENT_MESSAGE);
        } catch {
          updateBotMessage(completeMessage);
        }
        setIsDataMessage(false);
        return;
      }
      if (!response.text) return;

      setResponseGenerating(true);
      completeMessage += response.text;
      if (isDataMessage) return;

      if (completeMessage.startsWith('{')) {
        setIsDataMessage(true);
      } else {
        updateBotMessage(completeMessage);
      }
    };
    eventSource.onerror = () => {
      eventSource.close();
      setResponseGenerating(false);
    };
  };

  useEffect(() => {
    const fetchToken = async () => {
      const token = await auth.currentUser?.getIdToken();
      setToken(token);
    };
    fetchToken();
  }, []);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [transcriptMessages]);

  return (
    <div className="flex justify-between">
      <div className="flex flex-col flex-1 justify-between border border-neutral-200 rounded-lg p-4 bg-neutral-50 min-h-[600px] max-h-[600px] px-2">
        {transcriptMessages.length > 0 && (
          <div
            className="space-y-4 overflow-y-auto px-2"
            ref={messagesContainerRef}
          >
            {transcriptMessages.map((msg, index) => {
              const isHuman = msg.speaker === 'HUMAN';

              return (
                <div
                  key={index}
                  id={`message-${index}`}
                  className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={cn(
                      `max-w-[80%] rounded-lg px-4 py-2 cursor-pointer transition-opacity ${
                        isHuman
                          ? 'bg-primary text-neutral-0 hover:opacity-80'
                          : 'bg-neutral-0 text-neutral-800 border border-neutral-200 hover:opacity-60'
                      }`,
                    )}
                  >
                    <div
                      className={`flex ${isHuman ? 'justify-end' : 'justify-start'} text-xs opacity-70 mb-1`}
                    >
                      {msg.timestamp} - {msg.speaker}
                    </div>
                    <div className="whitespace-pre-wrap">{msg.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {transcriptMessages.length === 0 && (
          <div className="text-neutral-500 italic">
            Get started by typing a message
          </div>
        )}
        <Input
          ref={inputRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message"
          inputAction={() => {
            setTranscriptMessages((prev) => [
              ...prev,
              {
                timestamp: new Date().toISOString(),
                speaker: 'HUMAN',
                message,
              },
            ]);
            addBotThinkingMessage();
            fetchBotResponse(message);
            setMessage('');
          }}
          icon={<PaperPlaneRight size={20} className="mt-4" />}
          disabled={responseGenerating}
          className="mt-4"
        />
      </div>
      <div>
        <AgentCaller agent={{ ...(agent as Agent) }} />
      </div>
    </div>
  );
};
